import { useState, useEffect, useCallback } from 'react';
import { articleService, editionService, searchService, statsService, authorService } from '../services/api';

// Hook para manejar estados de carga
export function useAsync(asyncFunction, immediate = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...params) => {
    try {
      setLoading(true);
      setError(null);
      const result = await asyncFunction(...params);
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [asyncFunction]);

  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, []);

  return { data, loading, error, execute };
}

// Hook para artículos
export function useArticles(params = {}) {
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchArticles = useCallback(async (newParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      const result = await articleService.getAll({ ...params, ...newParams });
      setArticles(result.data);
      setPagination({
        page: result.page,
        totalPages: result.totalPages,
        total: result.total,
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchArticles();
  }, []);

  return { articles, pagination, loading, error, refetch: fetchArticles };
}

// Hook para un artículo individual
export function useArticle(slugOrId) {
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let articleData;
        if (typeof slugOrId === 'number' || !isNaN(parseInt(slugOrId))) {
          articleData = await articleService.getById(slugOrId);
        } else {
          articleData = await articleService.getBySlug(slugOrId);
        }
        
        setArticle(articleData);

        // Cargar artículos relacionados
        const related = await articleService.getRelated(articleData.id);
        setRelatedArticles(related);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (slugOrId) {
      fetchArticle();
    }
  }, [slugOrId]);

  return { article, relatedArticles, loading, error };
}

// Hook para ediciones
export function useEditions(params = {}) {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEditions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await editionService.getAll(params);
      setEditions(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchEditions();
  }, []);

  return { editions, loading, error, refetch: fetchEditions };
}

// Hook para una edición individual
export function useEdition(id) {
  const [edition, setEdition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEdition = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await editionService.getById(id);
        setEdition(result);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEdition();
    }
  }, [id]);

  return { edition, loading, error };
}

// Hook para búsqueda
export function useSearch() {
  const [results, setResults] = useState({ articles: [], categories: [], authors: [] });
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const search = useCallback(async (searchQuery, options = {}) => {
    if (!searchQuery.trim()) {
      setResults({ articles: [], categories: [], authors: [] });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setQuery(searchQuery);
      const result = await searchService.search(searchQuery, options);
      setResults(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults({ articles: [], categories: [], authors: [] });
  }, []);

  return { results, query, loading, error, search, clearSearch };
}

// Hook para estadísticas
export function useStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await statsService.getOverview();
        setStats(result);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, loading, error };
}

// Hook para autor
export function useAuthor(authorId) {
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAuthor = async () => {
      try {
        setLoading(true);
        setError(null);
        const [authorData, authorArticles] = await Promise.all([
          authorService.getById(authorId),
          authorService.getArticles(authorId),
        ]);
        setAuthor(authorData);
        setArticles(authorArticles);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (authorId) {
      fetchAuthor();
    }
  }, [authorId]);

  return { author, articles, loading, error };
}

// Hook para debounce
export function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// Hook para detectar scroll
export function useScrollPosition() {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isScrollingUp, setIsScrollingUp] = useState(true);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrollingUp(currentScrollY < lastScrollY);
      setScrollPosition(currentScrollY);
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return { scrollPosition, isScrollingUp };
}

// Hook para el formato de fecha
export function useFormattedDate(dateString) {
  const [formatted, setFormatted] = useState('');

  useEffect(() => {
    if (dateString) {
      const date = new Date(dateString);
      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      };
      setFormatted(date.toLocaleDateString('es-ES', options));
    }
  }, [dateString]);

  return formatted;
}
